import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { OpticalJobStatus, PERMISSIONS, type OpticalOrderCancellation } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Card, CardHeader } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { useBranch } from '@/hooks/useBranch';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { useCustomerQuery } from '@/modules/customers/api/customers.api';
import { useUnsavedChangesGuard } from '@/modules/medical/useUnsavedChangesGuard';
import { useSalesOrderQuery } from '@/modules/sales/api/sales.api';
import { useOpticalJobCollectionQuery, useOpticalJobQuery } from '../api/opticalJobs.api';
import {
  useDispensingPrescriptionsQuery,
  useOrderOpticalJobsQuery,
} from '../api/opticalOrders.api';
import { JobProgress } from '../components/JobProgress';
import { JobTimeline } from '../components/JobTimeline';
import { CancelOrderCard } from '../components/job/CancelOrderCard';
import { JobItemsCard } from '../components/job/JobItemsCard';
import { JobLabCard } from '../components/job/JobLabCard';
import { JobNextStep } from '../components/job/JobNextStep';
import { JobPaymentCard } from '../components/job/JobPaymentCard';
import { JobPrescriptionCard } from '../components/job/JobPrescriptionCard';
import { PrescriptionCorrection } from '../components/job/PrescriptionCorrection';
import { trackingErrorMessage } from '../tracking/errors';

const linkClass =
  'text-ink border-line hover:bg-canvas inline-flex h-10 items-center justify-center gap-2 rounded-lg border bg-white px-4 text-sm font-medium transition-colors';

type Notice = { tone: 'success' | 'info' | 'warning'; text: string };

/**
 * One optical job, from the counter to the customer's hands.
 *
 * Named by the job's id; its reference is for people. Everything is read from the job's snapshots, so what is shown is what was sold and made. The actions offered are the one step forward the job allows now and whatever the user's permissions allow of preparing, collecting and cancelling — each explicit, none taken by a refresh. Prescription values are shown read-only and live in memory only; a LEGACY job is shown for what it records and continued from its order page.
 */
export function OpticalJobPage() {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const { can } = usePermissions();
  const { activeBranch, branches } = useBranch();
  const { jobId = '' } = useParams<{ jobId: string }>();

  const [notice, setNotice] = useState<Notice | null>(null);
  const [correcting, setCorrecting] = useState<null | 'clinical' | 'any'>(null);
  const [rxDirty, setRxDirty] = useState(false);
  const [labDirty, setLabDirty] = useState(false);
  const [collectOpen, setCollectOpen] = useState(false);
  const [cancelled, setCancelled] = useState<OpticalOrderCancellation | null>(null);

  const job = useOpticalJobQuery(jobId);
  const data = job.data;
  const canMoney = can(PERMISSIONS.SALES_VIEW);
  const collection = useOpticalJobCollectionQuery(jobId, canMoney && Boolean(data));
  const order = useSalesOrderQuery(canMoney && data ? data.orderId : undefined);
  // The order's other jobs matter only to someone who may cancel it: they decide whether the whole order still can be.
  const mayCancel = can(PERMISSIONS.OPTICAL_CANCEL) && canMoney;
  const orderJobs = useOrderOpticalJobsQuery(mayCancel ? data?.orderId : undefined);
  const customer = useCustomerQuery(
    data?.customerId && can(PERMISSIONS.CUSTOMERS_VIEW) ? data.customerId : undefined,
  );

  const clinicalId = data?.prescription?.clinicalPrescriptionId ?? null;
  const prepare =
    data?.status === OpticalJobStatus.ORDERED &&
    data.origin === 'CHECKOUT' &&
    can(PERMISSIONS.OPTICAL_UPDATE);
  const dispensing = useDispensingPrescriptionsQuery(
    data?.customerId ?? '',
    true,
    Boolean(clinicalId) && data?.status === OpticalJobStatus.ORDERED && data.origin === 'CHECKOUT',
  );
  const clinicalUsability =
    clinicalId && dispensing.data
      ? (dispensing.data.find((entry) => entry.clinicalPrescriptionId === clinicalId)?.usability ??
        null)
      : null;
  const clinicalUnusable = clinicalUsability !== null && clinicalUsability !== 'USABLE';

  const guard = useUnsavedChangesGuard(rxDirty || labDirty);

  if (job.isPending) return <LoadingState />;
  if (job.isError || !data) {
    return (
      <div className="space-y-4">
        <Alert tone="error">{trackingErrorMessage(t, job.error)}</Alert>
        <Link to="/sales/optical-jobs" className={linkClass}>
          <ArrowLeft className="size-4 rtl:rotate-180" aria-hidden />
          {t('opticalOrders.job.backToQueue')}
        </Link>
      </div>
    );
  }

  const branchName = branches.find((branch) => branch.id === data.branchId)?.name ?? null;
  const customerName = customer.data?.displayName ?? order.data?.customerName ?? null;
  const otherBranchName =
    activeBranch && activeBranch.id === data.branchId
      ? null
      : (branchName ?? t('opticalOrders.collect.itsBranch'));

  const reread = () => {
    void job.refetch();
    void collection.refetch();
    void orderJobs.refetch();
  };
  // A change refused because the job moved on meanwhile: read it again and say so; the change is never resent.
  const conflicted = () => {
    reread();
    setNotice({ tone: 'warning', text: t('opticalOrders.job.conflict') });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('opticalOrders.job.title', { reference: data.reference })}
        subtitle={[customerName, branchName].filter(Boolean).join(' · ')}
        actions={
          <>
            <Link to="/sales/optical-jobs" className={linkClass}>
              <ArrowLeft className="size-4 rtl:rotate-180" aria-hidden />
              {t('opticalOrders.job.backToQueue')}
            </Link>
            {canMoney ? (
              <Link to={`/sales/optical-orders/${data.orderId}`} className={linkClass}>
                <ExternalLink className="size-4" aria-hidden />
                {t('opticalOrders.job.order', { reference: data.orderReference })}
              </Link>
            ) : null}
          </>
        }
      />

      <div aria-live="polite">
        {notice ? <Alert tone={notice.tone}>{notice.text}</Alert> : null}
      </div>

      {cancelled ? (
        <Card>
          <CardHeader
            title={t('opticalOrders.cancel.doneTitle', { reference: cancelled.orderReference })}
            subtitle={t('opticalOrders.cancel.doneHint')}
          />
          <dl className="space-y-2 px-5 py-4 text-sm">
            <div className="flex flex-wrap justify-between gap-2">
              <dt className="text-muted">{t('opticalOrders.cancel.doneJobs')}</dt>
              <dd className="text-ink font-mono" dir="ltr">
                {cancelled.jobs.map((entry) => entry.reference).join(', ')}
              </dd>
            </div>
            {cancelled.refunds.map((refund) => (
              <div key={refund.id} className="flex flex-wrap justify-between gap-2">
                <dt className="text-muted">
                  {t('opticalOrders.cancel.doneRefund', {
                    method: t(`sales.methods.${refund.method}`),
                  })}
                </dt>
                <dd className="text-ink" dir="ltr">
                  {format(refund.amountMinor)}
                </dd>
              </div>
            ))}
            {cancelled.creditNote ? (
              <div className="flex flex-wrap justify-between gap-2">
                <dt className="text-muted">{t('opticalOrders.cancel.doneCreditNote')}</dt>
                <dd className="text-ink font-mono" dir="ltr">
                  {cancelled.creditNote.reference}
                </dd>
              </div>
            ) : null}
            <div className="flex flex-wrap justify-between gap-2">
              <dt className="text-muted">
                {t('opticalOrders.cancel.doneStock', { count: cancelled.stockReversals })}
              </dt>
              <dd />
            </div>
            <div className="flex flex-wrap justify-between gap-2">
              <dt className="text-ink font-medium">{t('opticalOrders.cancel.doneBalance')}</dt>
              <dd className="text-ink font-semibold" dir="ltr">
                {format(cancelled.balance.outstandingMinor)}
              </dd>
            </div>
          </dl>
        </Card>
      ) : null}

      <JobProgress status={data.status} />

      {data.status === OpticalJobStatus.CANCELLED && !cancelled ? (
        <Alert tone="warning">{t('opticalOrders.job.cancelledNotice')}</Alert>
      ) : null}

      <JobNextStep
        job={data}
        customerName={customerName}
        collection={canMoney ? (collection.data ?? null) : null}
        clinicalUnusable={clinicalUnusable}
        unsavedChanges={rxDirty || labDirty}
        onMoved={(text) => setNotice({ tone: 'success', text })}
        onConflict={conflicted}
        onCollect={
          can(PERMISSIONS.PAYMENTS_CREATE) && !otherBranchName && !collection.data?.plan
            ? () => setCollectOpen(true)
            : undefined
        }
        otherBranchName={otherBranchName}
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-6">
          <JobItemsCard job={data} />
          {correcting && data.customerId ? (
            <PrescriptionCorrection
              job={data}
              customerId={data.customerId}
              clinicalFirst={correcting === 'clinical'}
              onDirtyChange={setRxDirty}
              onClose={() => {
                setCorrecting(null);
                setRxDirty(false);
              }}
              onSaved={() => {
                setCorrecting(null);
                setRxDirty(false);
                void dispensing.refetch();
                setNotice({ tone: 'success', text: t('opticalOrders.job.prescriptionSaved') });
              }}
              onConflict={() => {
                setCorrecting(null);
                setRxDirty(false);
                conflicted();
              }}
            />
          ) : (
            <JobPrescriptionCard
              job={data}
              clinicalUsability={clinicalUsability}
              onCorrect={
                prepare && data.customerId
                  ? () => setCorrecting(clinicalUnusable ? 'clinical' : 'any')
                  : undefined
              }
            />
          )}
          <JobLabCard
            job={data}
            canEdit={Boolean(prepare)}
            onDirtyChange={setLabDirty}
            onSaved={(text) => setNotice({ tone: 'success', text })}
            onConflict={conflicted}
          />
        </div>

        <div className="min-w-0 space-y-6">
          {canMoney ? (
            <JobPaymentCard
              jobId={data.id}
              collection={collection.data}
              isLoading={collection.isPending}
              loadError={collection.error}
              canCollect={can(PERMISSIONS.PAYMENTS_CREATE)}
              otherBranchName={otherBranchName}
              cancelled={data.status === OpticalJobStatus.CANCELLED}
              collectOpen={collectOpen}
              onCollectOpenChange={setCollectOpen}
              onCollected={(text) => setNotice({ tone: 'success', text })}
            />
          ) : null}
          <Card>
            <CardHeader title={t('opticalOrders.tracking.timeline')} />
            <div className="px-5 py-4">
              <JobTimeline history={data.history} />
            </div>
          </Card>
          {mayCancel && order.data && orderJobs.data && !cancelled ? (
            <CancelOrderCard
              order={order.data}
              jobs={orderJobs.data}
              otherBranchName={otherBranchName}
              onCancelled={(result) => {
                setCancelled(result);
                reread();
              }}
            />
          ) : null}
        </div>
      </div>

      {guard.isBlocked ? (
        <ConfirmDialog
          isOpen
          isDestructive
          title={t('opticalOrders.job.leaveTitle')}
          message={t('opticalOrders.job.leaveMessage')}
          confirmLabel={t('opticalOrders.leave.confirm')}
          cancelLabel={t('opticalOrders.leave.stay')}
          onCancel={guard.stay}
          onConfirm={guard.leave}
        />
      ) : null}
    </div>
  );
}
